import React, { useState } from "react";
import awardsData from "../data/barbequeen-awards-achievements.json";
import SEO from "../components/SEO";
import { awardsPageSchema } from "../data/schemas";

const sections = awardsData.page.sections;

const AwardsPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  return (
    <div className="min-h-screen bg-[#f9f5f0]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Barbequeen Awards & Achievements | Recognised Since 1999"
        description="Explore the awards and achievements earned by Barbequeen Restaurant since 1999, celebrating our grills, biryani, service and quality across Tamil Nadu today."
        keywords="Barbequeen awards, Barbequeen achievements, best restaurant awards Salem, Barbequeen recognition, award winning restaurant Salem"
        robots="index, follow, max-image-preview:large"
        canonical="https://barbequeen.com/awards-achievements"
        og={{
          type: "website",
          url: "https://barbequeen.com/awards-achievements",
          title: "Barbequeen Awards & Achievements | Recognised Since 1999",
          description: "The awards and achievements earned by Barbequeen Restaurant since 1999 — recognition for our grills, biryani, service and quality.",
          image: "https://barbequeen.com/images/awards-og.jpg",
          imageWidth: "1200",
          imageHeight: "630",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={awardsPageSchema}
      />

      {/* ── Lightbox Modal ── */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col items-center animate-fade-in">
            <img
              src={lightboxImage.full}
              alt={lightboxImage.caption}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1531058020387-3be344559be6?q=80&w=1200&auto=format&fit=crop";
              }}
            />
            <div className="mt-4 text-center">
              <span className="px-2.5 py-0.5 bg-[#9b1c2e] text-white text-[10px] uppercase font-bold tracking-widest rounded-full mb-1.5 inline-block">
                {lightboxImage.group}
              </span>
              <p className="text-white text-sm font-semibold">{lightboxImage.caption}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        <img
          src="https://images.unsplash.com/photo-1543007630-9710e4a00a20?q=80&w=1600&auto=format&fit=crop"
          alt="Awards and Achievements"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Our Milestones</p>
          <h1 className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
            Awards & Achievements
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Celebrating the honors and awards earned through our commitment to culinary excellence since 1999.
          </p>
        </div>
      </section>

      {/* ── Content Sections ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 pb-24">
        <div className="space-y-24">
          
          {sections.map((section, idx) => (
            <div key={idx} className="space-y-8">
              
              {/* Centered Section Header */}
              <div className="text-center max-w-xl mx-auto mb-10">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#9b1c2e] mb-2">
                  Recognitions
                </p>
                <h3
                  className="text-2xl sm:text-3xl font-bold text-gray-900 uppercase leading-snug tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {section.title}
                </h3>
                <div className="w-12 h-0.5 bg-[#9b1c2e] mx-auto mt-4 mb-2" />
                <span className="text-[11px] text-gray-400 font-sans tracking-wide block">
                  {section.gallery.length} Highlighted Item{section.gallery.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Photos Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.gallery.map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    onClick={() => setLightboxImage(item)}
                    className="group cursor-pointer rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 relative aspect-[4/3]"
                  >
                    <img
                      src={item.thumbnail}
                      alt={item.caption}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1531058020387-3be344559be6?q=80&w=600&auto=format&fit=crop";
                      }}
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          ))}

        </div>
      </section>

    </div>
  );
};

export default AwardsPage;

